import React, { useEffect, useState } from 'react';
import {
  CheckCircle as CheckIcon,
  CleaningServices as CleanupIcon,
  Edit as EditIcon,
  Feedback as FeedbackIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import {
  type RejectionModalContentProps,
  RejectionReason,
  type TemplateCleanupPayload,
} from '../../types';
import { TemplateCleanupForm } from '../edit/TemplateCleanupForm';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'edit' | 'feedback' | 'confirmation' | 'template_confirmation';

/**
 * Template cleanup-specific rejection modal content component
 * Provides options specific to template cleanup suggestion rejections
 */
export const TemplateCleanupRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onAccept,
  isLoading = false,
}) => {
  const { action } = context;
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [editedPayload, setEditedPayload] = useState<TemplateCleanupPayload>(
    action.action_payload as TemplateCleanupPayload,
  );
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const [pendingRejectionReason, setPendingRejectionReason] = useState<RejectionReason | null>(
    null,
  );

  const templateData = action.action_payload as TemplateCleanupPayload;
  const sectionsToRemoveCount = templateData.sections.filter(s => s.should_remove).length;

  // Auto-close countdown for confirmation steps
  useEffect(() => {
    if (
      (currentStep === 'confirmation' || currentStep === 'template_confirmation') &&
      autoCloseCountdown === null
    ) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else if (autoCloseCountdown === 0 && pendingRejectionReason) {
      // When countdown reaches 0, trigger rejection
      if (currentStep === 'confirmation') {
        onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
      } else if (currentStep === 'template_confirmation') {
        onReject(RejectionReason.NOT_FOR_THIS_TEMPLATE, {
          templateId: templateData.template_id,
        });
      }
    }
  }, [autoCloseCountdown, onReject, pendingRejectionReason, currentStep, templateData.template_id]);

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        setCurrentStep('edit');
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setPendingRejectionReason(reason);
        setCurrentStep('confirmation');
        break;
      case RejectionReason.NOT_FOR_THIS_TEMPLATE:
        setPendingRejectionReason(reason);
        setCurrentStep('template_confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleEditConfirm = () => {
    // Accept with the edited payload
    onAccept?.(action.action_id, editedPayload);
  };

  const handleEditReject = () => {
    // Still reject after editing
    onReject(RejectionReason.WRONG_SUGGESTION);
  };

  const handleFeedbackSubmit = () => {
    // Pass rejection with feedback
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
    setPendingRejectionReason(null);
    // Reset edited payload to original
    setEditedPayload(templateData);
  };

  // Type guard to ensure we have template cleanup data
  if (action.action_type !== 'template_cleanup') {
    console.error('TemplateCleanupRejection received non-template-cleanup action');
    return null;
  }

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Template Summary */}
              <Alert severity="info" icon={<CleanupIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested template cleanup:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    Remove {sectionsToRemoveCount} section{sectionsToRemoveCount !== 1 ? 's' : ''}{' '}
                    from <strong>{templateData.template_name}</strong>
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        This cleanup suggestion is incorrect
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The sections selected for removal are not what I want to remove
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don&apos;t want template cleanup suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off template cleanup suggestions for all future notes
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.NOT_FOR_THIS_TEMPLATE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Don&apos;t show me suggestions for this template
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off cleanup suggestions for {templateData.template_name} only
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting this suggestion
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'edit' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Template Cleanup
                </Typography>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  Pick which sections you&apos;d like to remove, then accept the cleanup.
                </Typography>
              </Alert>

              {/* Template Cleanup Form */}
              <TemplateCleanupForm
                payload={editedPayload}
                onChange={setEditedPayload}
                readOnly={false}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleEditReject} variant="outlined" color="error">
                  Still Reject
                </Button>
                <Button onClick={handleEditConfirm} variant="contained" color="primary">
                  Accept Changes
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting this template cleanup
                suggestion.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => {
                  setFeedbackText(e.target.value);
                }}
                placeholder="Please describe why this template cleanup suggestion wasn't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.OTHER);
                  }}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Template Cleanup Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive template cleanup suggestions for any templates. You can
                re-enable this feature anytime in your preferences.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  // Pass rejection when user clicks "Close"
                  onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'template_confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Template Cleanup Disabled for {templateData.template_name}
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive cleanup suggestions for this template. You will still
                receive suggestions for other templates.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  // Pass rejection when user clicks "Close"
                  onReject(RejectionReason.NOT_FOR_THIS_TEMPLATE, {
                    templateId: templateData.template_id,
                  });
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
